/**
 * Centroide de parcela + point-in-polygon sobre AOI (sin Turf).
 * Usado por dashboard (filtro San Pedro) y equipo captura.
 */

/** Posición GeoJSON [lng, lat] o con Z. */
type Position = [number, number] | [number, number, number];

type PolygonGeom = { type: 'Polygon'; coordinates: Position[][] };
type MultiPolygonGeom = { type: 'MultiPolygon'; coordinates: Position[][][] };

function ringCentroid(ring: Position[]): [number, number] {
  if (ring.length === 0) return [0, 0];
  let sx = 0;
  let sy = 0;
  const n = ring.length;
  for (const p of ring) {
    sx += p[0];
    sy += p[1];
  }
  return [sx / n, sy / n];
}

function centroidFromPolygonCoords(coords: PolygonGeom['coordinates']): [number, number] {
  const outer = coords[0];
  if (!outer?.length) return [0, 0];
  return ringCentroid(outer);
}

function centroidFeature(geom: unknown): [number, number] | null {
  if (!geom || typeof geom !== 'object') return null;
  const g = geom as { type?: string; coordinates?: unknown };
  if (g.type === 'Polygon' && Array.isArray(g.coordinates)) {
    return centroidFromPolygonCoords(g.coordinates as PolygonGeom['coordinates']);
  }
  if (g.type === 'MultiPolygon' && Array.isArray(g.coordinates)) {
    const first = (g.coordinates as MultiPolygonGeom['coordinates'])[0]?.[0];
    if (!first?.length) return null;
    return ringCentroid(first);
  }
  return null;
}

function pointInRing(lng: number, lat: number, ring: Position[]): boolean {
  if (ring.length < 3) return false;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const pi = ring[i];
    const pj = ring[j];
    if (!pi || !pj) continue;
    const xi = pi[0];
    const yi = pi[1];
    const xj = pj[0];
    const yj = pj[1];
    const denom = (yj - yi) || 1e-12;
    const intersect =
      yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / denom + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

function pointInPolygonRings(lng: number, lat: number, rings: Position[][]): boolean {
  if (rings.length === 0) return false;
  const outer = rings[0];
  if (!outer || !pointInRing(lng, lat, outer)) return false;
  for (let h = 1; h < rings.length; h++) {
    const hole = rings[h];
    if (hole && pointInRing(lng, lat, hole)) return false;
  }
  return true;
}

function pointInMultiPolygon(lng: number, lat: number, mp: MultiPolygonGeom): boolean {
  for (const poly of mp.coordinates) {
    if (poly.length > 0 && pointInPolygonRings(lng, lat, poly)) {
      return true;
    }
  }
  return false;
}

function pointInGeometry(lng: number, lat: number, geom: PolygonGeom | MultiPolygonGeom): boolean {
  if (geom.type === 'Polygon') {
    return pointInPolygonRings(lng, lat, geom.coordinates);
  }
  return pointInMultiPolygon(lng, lat, geom);
}

/**
 * Lista de `parcela_id` cuyo centroide cae dentro del polígono AOI (primera
 * feature Polygon/MultiPolygon del collection).
 */
export function parcelaIdsDentroDeAoi(
  parcelas: { features: ReadonlyArray<{ properties?: unknown; geometry?: unknown }> },
  aoi: { features: ReadonlyArray<{ geometry?: unknown }> },
): string[] {
  let geom: PolygonGeom | MultiPolygonGeom | null = null;
  for (const f of aoi.features) {
    const g = f.geometry;
    if (g != null && typeof g === 'object' && 'type' in g) {
      const t = (g as { type: string }).type;
      if (t === 'Polygon' || t === 'MultiPolygon') {
        geom = g as PolygonGeom | MultiPolygonGeom;
        break;
      }
    }
  }
  if (!geom) {
    return [];
  }
  const out: string[] = [];
  for (const f of parcelas.features) {
    const c = centroidFeature(f.geometry);
    if (!c) continue;
    const [lng, lat] = c;
    if (!pointInGeometry(lng, lat, geom)) continue;
    const props = f.properties as Record<string, unknown> | null | undefined;
    const pid = props?.parcela_id;
    if (pid != null && String(pid).trim() !== '') {
      out.push(String(pid));
    }
  }
  return out;
}
